import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { clsx } from 'clsx';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import Modal from 'components/Modal/Modal';
import { DATE_LOCALE } from 'constants/general';
import { BUTTON_VARIANT, ICON_BUTTON_SIZES } from 'constants/interfaces';
import { useDeleteAiChatMutation, useGetAiChatsQuery } from 'store/slices/aiChats/apis/aiChatsApi';
import { formatNotificationTime } from 'utils/dateFormatters';

import type { ReactElement } from 'react';
import type IAiChatHistory from './interfaces/IAiChatHistory';

import styles from './sass/AiChatHistory.module.scss';

/** Clock time, for a chat last active today. */
const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

/**
 * When a chat was last active, at the resolution that helps pick one out.
 *
 * `formatNotificationTime` counts in whole days and rounds up, so everything from today — which is
 * most of a history list, and always the chat the user is in — comes back as "1 day ago". Today is
 * shown as a time instead, and anything older is left to the shared formatter.
 */
const formatChatTime = (updatedAt: string): string => {
  const updated = new Date(updatedAt);
  const isToday = updated.toDateString() === new Date().toDateString();

  return isToday
    ? updated.toLocaleTimeString(DATE_LOCALE, TIME_FORMAT)
    : formatNotificationTime(updated);
};

/**
 * Every chat the user has, most recently active first, to pick one to carry on or delete, and a
 * way to start a fresh one.
 *
 * The list is read only while it is on screen, so a panel that is never opened onto the history
 * costs no request. It is shown in the order the server sends it: the server sorts by last
 * activity, which the client cannot reproduce from ids. Picking a chat hands the id back: this
 * component holds no session of its own.
 *
 * Starting a new chat sits above the list and stays there whatever the list came to, since it
 * needs nothing the read supplies — an empty history is the state it is most useful in.
 */
function AiChatHistory({
  selectedChatId, onSelect, onNewChat, onDeleted, className,
}: IAiChatHistory): ReactElement {
  const { t } = useTranslation();
  const { data: chats, isLoading, error } = useGetAiChatsQuery();
  const [deleteAiChat, { isLoading: isDeleting }] = useDeleteAiChatMutation();
  // The chat a delete is being confirmed for. Nothing is sent until the user says yes.
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<boolean>(false);

  const isEmpty = !isLoading && !error && !chats?.length;

  const handleCancelDelete = () => setPendingDeleteId(null);

  const handleConfirmDelete = async () => {
    if (pendingDeleteId === null) {
      return;
    }

    setDeleteError(false);

    try {
      await deleteAiChat(pendingDeleteId).unwrap();
      onDeleted?.(pendingDeleteId);
    } catch {
      setDeleteError(true);
    } finally {
      setPendingDeleteId(null);
    }
  };

  return (
    <div className={clsx(styles.root, className)}>
      <Button
        className={styles.newChat}
        variant={BUTTON_VARIANT.primary}
        onClick={onNewChat}
        startIcon={<PlusIcon />}
        title={t('aiChat.newChat')}
      />

      {isLoading ? <p className={styles.note}>{t('aiChat.historyLoading')}</p> : null}
      {error ? <p className={styles.error}>{t('aiChat.historyFailed')}</p> : null}
      {deleteError ? <p className={styles.error}>{t('aiChat.deleteChatFailed')}</p> : null}
      {isEmpty ? <p className={styles.note}>{t('aiChat.historyEmpty')}</p> : null}

      <ul className={styles.list}>
        {(chats || []).map(({ id, updatedAt, lastMessage }) => (
          <li key={id} className={styles.row}>
            <button
              type="button"
              className={clsx(styles.item, { [styles.itemSelected]: id === selectedChatId })}
              onClick={() => onSelect(id)}
              aria-current={id === selectedChatId || undefined}
            >
              {/* A chat older than the column has no message to show, so it keeps its number. */}
              <span className={styles.itemName}>{lastMessage ?? t('aiChat.chatLabel', { id })}</span>
              <span className={styles.itemTime}>{formatChatTime(updatedAt)}</span>
            </button>
            <IconButton
              className={styles.delete}
              icon={<TrashIcon />}
              size={ICON_BUTTON_SIZES.small}
              onClick={() => setPendingDeleteId(id)}
              ariaLabel={t('aiChat.deleteChat')}
            />
          </li>
        ))}
      </ul>

      <Modal
        open={pendingDeleteId !== null}
        title={t('aiChat.deleteChat')}
        description={t('aiChat.deleteChatConfirm')}
        closeTitle={t('common.cancel')}
        confirmTitle={t('common.delete')}
        confirmIcon={<TrashIcon />}
        confirmVariant={BUTTON_VARIANT.error}
        onReject={handleCancelDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}

export default AiChatHistory;
